import * as React from "react";
import { VingleURL } from "@vingle/wtf-sdk/url";
// components
import { Link } from "react-router-dom";
import Popover from "@material-ui/core/Popover";
import { FormattedMessage } from "react-intl";
import Icon from "common/icons";
import SquareStackedImage from "common/components/squareStackedImage";
import NumberFormatter from "@vingle/wtf-sdk/components/numberFormatter";
import CollectionFollowButton from "common/components/collectionFollowButton";
// constants
import { STATUS_OF_DELETED_USER } from "@vingle/wtf-sdk/constants/default";
// styles
const styles = require("./collectionItem.scss");

//helpers
import cx from "classnames";
import hexToRgba from "@vingle/wtf-sdk/helpers/hexToRgba";

interface ICollectionItemProps {
  collection: Vingle.Collection.ICollection;
  mine: boolean;
  redirectToCollectionShow: (collection: Vingle.Id) => void;
}
const CollectionItem = ({
  collection,
  mine,
  redirectToCollectionShow,
}: ICollectionItemProps) => {
  const [isMoreOpen, setIsMoreOpen] = React.useState(false);
  const moreMenuAnchor = React.useRef<HTMLAnchorElement>(null);

  const imageWrapper = React.useMemo(() => {
    if (collection.image_urls.s) {
      return (
        <SquareStackedImage
          width={90}
          height={90}
          src={collection.image_urls.s}
        />
      );
    } else {
      return (
        <SquareStackedImage
          width={90}
          height={90}
          color={collection.color ? hexToRgba(collection.color) : undefined}
          title={collection.title}
        />
      );
    }
  }, [collection.image_urls.s]);
  const collectionSmallInfo: React.ReactNode = React.useMemo(() => {
    if (!mine) {
      return null;
    }
    return (
      <div className={styles.graySmallText}>
        {collection.default && <FormattedMessage id="profile.text_default" />}
        {!collection.public && (
          <span className={styles.privateElement}>
            <Icon icon="INTERESTS_PRIVATE_MEDIUM" />
            <span>
              <FormattedMessage id="profile.text_private" />
            </span>
          </span>
        )}
        <FormattedMessage
          id="profile.text_follower"
          values={{
            follower_count: collection.followers_count.toString(),
          }}
        />
      </div>
    );
  }, [collection.public, collection.default, mine]);

  const onCollectionItemClick = React.useCallback(() => {
    redirectToCollectionShow(collection.id);
  }, [collection.id]);
  const openMoreMenu = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      setIsMoreOpen(true);
      stopPropagation(e);
    },
    [],
  );
  const closeMoreMenu = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      setIsMoreOpen(false);
      stopPropagation(e);
    },
    [],
  );
  const stopPropagation = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
    },
    [],
  );

  return (
    <div className={styles.itemWrapper} onClick={onCollectionItemClick}>
      <div className={styles.left}>
        <div className={styles.imageWrapper}>{imageWrapper}</div>

        <div className={styles.info}>
          <div className={styles.title}>{collection.title}</div>
          <div className={styles.smallText}>
            by
            <Link
              to={new VingleURL.UserShow({
                username: collection.author.username,
              }).toString()}
              className={styles.author}
              onClick={stopPropagation}
            >
              {collection.author.username}
            </Link>
          </div>
          {collectionSmallInfo ? (
            collectionSmallInfo
          ) : (
            <div className={styles.graySmallText}>
              <FormattedMessage
                id="profile.text_follower"
                values={{
                  follower_count: (
                    <NumberFormatter
                      num={collection.followers_count || 0}
                      digits={0}
                    />
                  ),
                }}
              />
            </div>
          )}
        </div>
      </div>
      <div className={cx(styles.right, { [styles.middleOfHeight]: !mine })}>
        {mine && (
          <a onClick={openMoreMenu} ref={moreMenuAnchor}>
            <Icon icon="PROFILE_MORE_BUTTON" />
          </a>
        )}
        <FollowButton collection={collection} mine={mine} />
      </div>
      <Popover
        open={isMoreOpen}
        anchorEl={moreMenuAnchor.current}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        onClose={closeMoreMenu}
        className={styles.menuPopover}
        elevation={2}
        transitionDuration={0}
      >
        <div className={styles.menuWrapper}>
          <div className={styles.menuItem}>
            <Link
              to={new VingleURL.CollectionEdit({
                collection: collection.id,
              }).toString()}
            >
              <FormattedMessage id="profile.text_edit_collection" />
            </Link>
          </div>
        </div>
      </Popover>
    </div>
  );
};

const FollowButton = ({
  collection,
  mine,
}: {
  collection: Vingle.Collection.ICollection;
  mine: boolean;
}) => {
  const isDeletedUser = collection.author.status === STATUS_OF_DELETED_USER;

  if (mine || isDeletedUser) {
    return null;
  }

  const isFollowing = Boolean(
    collection.relation && collection.relation.following,
  );
  return (
    <div className={styles.right}>
      <CollectionFollowButton
        isFollowing={isFollowing}
        collectionId={collection.id}
      />
    </div>
  );
};
export default CollectionItem;
